// Privy login and wallets. React lives only here; it renders nothing visible of its own.
// The rest of the app (plain JS pages) reaches it through the small bridge object below.
import { PrivyProvider, useLogin, useLogout, usePrivy, useWallets, type ConnectedWallet } from '@privy-io/react-auth';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import type { Address, EIP1193Provider } from 'viem';
import { clearWallet, currentWallet, setWallet } from './chain';
import { chain, config } from './config';

type Pending = { resolve: (ok: boolean) => void };

export const privyBridge = {
  ready: false,
  login: (): Promise<boolean> => Promise.resolve(false),
  logout: (): Promise<void> => Promise.resolve(),
};

let pending: Pending | null = null;

function pickWallet(wallets: ConnectedWallet[], linked: string[]): ConnectedWallet | undefined {
  // Prefer an external wallet the user linked, then the embedded one, then anything connected.
  const ext = wallets.find((w) => w.walletClientType !== 'privy' && linked.includes(w.address.toLowerCase()));
  return ext ?? wallets.find((w) => w.walletClientType === 'privy') ?? wallets[0];
}

function Bridge() {
  const { ready, authenticated, user } = usePrivy();
  const { wallets, ready: walletsReady } = useWallets();
  const active = useRef<string | null>(null);
  const { login } = useLogin({
    onError: () => {
      pending?.resolve(false);
      pending = null;
    },
  });
  const { logout } = useLogout();

  useEffect(() => {
    privyBridge.ready = ready;
    privyBridge.login = () =>
      new Promise<boolean>((resolve) => {
        if (currentWallet()) return resolve(true);
        pending = { resolve };
        login();
      });
    privyBridge.logout = async () => {
      active.current = null;
      clearWallet();
      await logout();
    };
  }, [ready, login, logout]);

  useEffect(() => {
    if (!ready || !walletsReady) return;
    if (!authenticated) {
      if (active.current) {
        active.current = null;
        clearWallet();
      }
      return;
    }
    const linked = (user?.linkedAccounts ?? []).filter((a: any) => a.type === 'wallet').map((a: any) => String(a.address).toLowerCase());
    const w = pickWallet(wallets, linked);
    if (!w) return; // embedded wallet still being created
    if (active.current === w.address) return;
    active.current = w.address;
    (async () => {
      const provider = (await w.getEthereumProvider()) as EIP1193Provider;
      const name = w.walletClientType === 'privy' ? 'Listed wallet' : w.meta?.name || 'Wallet';
      setWallet(w.address as Address, provider, name);
      pending?.resolve(true);
      pending = null;
    })().catch((e) => {
      console.error('[privy] wallet', e);
      pending?.resolve(false);
      pending = null;
    });
  }, [ready, walletsReady, authenticated, wallets, user]);

  return null;
}

export function mountPrivy() {
  const el = document.createElement('div');
  el.id = 'privy-root';
  document.body.appendChild(el);
  createRoot(el).render(
    <PrivyProvider
      appId={config.privyAppId}
      config={{
        loginMethods: ['wallet', 'email', 'google', 'twitter'],
        appearance: {
          theme: 'dark',
          accentColor: '#2440F0',
          logo: '/favicon.svg',
          landingHeader: 'Sign in to Listed',
          showWalletLoginFirst: true,
          walletChainType: 'ethereum-only',
          walletList: ['metamask', 'coinbase_wallet', 'rabby_wallet', 'detected_ethereum_wallets', 'wallet_connect'],
        },
        embeddedWallets: { ethereum: { createOnLogin: 'users-without-wallets' } },
        defaultChain: chain,
        supportedChains: [chain],
      }}
    >
      <Bridge />
    </PrivyProvider>,
  );
}
